import type { ReactNode } from 'react';
import { theme } from 'antd';
import { cn } from '@shared/lib/cn';

interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Brand-tinted empty state. Replaces antd's grey `<Empty>` illustration, which
 * reads as a broken image next to the app's coloured surfaces. The mark is
 * inline SVG so it inherits the theme instead of shipping a raster asset.
 */
function EmptyMark() {
  const { token } = theme.useToken();
  return (
    <svg width="112" height="88" viewBox="0 0 112 88" fill="none" aria-hidden>
      <defs>
        <linearGradient id="es-sheet" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2f5cf6" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      {/* ground shadow */}
      <ellipse cx="56" cy="76" rx="34" ry="6" fill={token.colorFillQuaternary} />
      {/* back sheet */}
      <rect
        x="26" y="14" width="60" height="50" rx="10"
        fill="url(#es-sheet)" stroke={token.colorBorderSecondary}
      />
      {/* front sheet */}
      <rect
        x="18" y="24" width="60" height="44" rx="10"
        fill={token.colorBgContainer} stroke={token.colorBorderSecondary}
      />
      <rect x="28" y="36" width="34" height="4" rx="2" fill="#2f5cf6" opacity="0.35" />
      <rect x="28" y="46" width="26" height="4" rx="2" fill={token.colorFill} opacity="0.5" />
      <rect x="28" y="56" width="18" height="4" rx="2" fill={token.colorFill} opacity="0.35" />
      {/* accent dot */}
      <circle cx="86" cy="26" r="9" fill="#8b5cf6" opacity="0.18" />
      <circle cx="86" cy="26" r="3.5" fill="#8b5cf6" opacity="0.6" />
    </svg>
  );
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex justify-center py-10', className)}>
      <div className="flex flex-col items-center gap-3 text-center">
        <EmptyMark />
        <span className="text-base font-semibold">{title}</span>
        {description ? (
          <span className="max-w-md text-sm opacity-70">{description}</span>
        ) : null}
        {action ? <div className="pt-1">{action}</div> : null}
      </div>
    </div>
  );
}
